import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { NAV_ICONS } from '../lib/navIcons'
import { useAuthStore } from '../hooks/useAuth'

const PRESETS = {
  'Mega Caps': ['SPY','QQQ','AAPL','MSFT','NVDA','AMZN','GOOGL','META','TSLA','BRK.B'],
  'Futures':   ['ES1!','NQ1!','RTY1!','YM1!','CL1!','GC1!','SI1!'],
  'Crypto':    ['BTC','ETH','SOL','XRP','DOGE'],
  'Mag 7':     ['AAPL','MSFT','NVDA','AMZN','GOOGL','META','TSLA'],
}

const STRENGTH_COLOR: Record<string, string> = {
  strong: 'var(--cash)', moderate: 'var(--amber)', weak: '#888', none: 'var(--edge)',
}
const STRENGTH_BG: Record<string, string> = {
  strong: 'rgba(133,196,124,.08)', moderate: 'rgba(212,168,67,.06)', weak: 'transparent', none: 'transparent',
}

// ─── Free tier demo ───────────────────────────────────────────────────────────
const DEMO_RESULTS = [
  { ticker: 'NVDA', price: 824.18, change: -11.40, changePct: '-1.37%', strength: 'strong',   signals: 4, zone: 'discount', eq: 798.40, pdh: 842.10, fvg: { bottom: 816.40, top: 822.80 }, ob: { bottom: 812.00, top: 818.50 } },
  { ticker: 'AAPL', price: 187.42, change:   1.23, changePct: '+0.66%', strength: 'moderate', signals: 2, zone: 'discount', eq: 191.20, pdh: 188.91, fvg: null, ob: { bottom: 184.80, top: 186.20 } },
  { ticker: 'SPY',  price: 521.34, change:   0.88, changePct: '+0.17%', strength: 'weak',     signals: 1, zone: 'premium',  eq: 518.60, pdh: 523.10, fvg: null, ob: null },
  { ticker: 'MSFT', price: 412.60, change:   3.14, changePct: '+0.77%', strength: 'none',     signals: 0, zone: 'premium',  eq: 408.90, pdh: 414.20, fvg: null, ob: null },
]

function FreePreview({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.3)', borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <img src="/icons/watchlist.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/>
        <div style={{ flex: 1, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--amber)', lineHeight: 1.5 }}>
          <strong>Preview mode</strong> — this is a sample scan. Upgrade to scan any list of tickers for ICT key levels.
        </div>
        <button onClick={onUpgrade} style={{ padding: '6px 16px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Upgrade — $9.99/mo →
        </button>
      </div>

      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px', opacity: .35, pointerEvents: 'none', userSelect: 'none' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Tickers to scan</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 11px', fontFamily: 'Syne Mono', fontSize: '.75rem', color: 'var(--muted)' }}>AAPL, MSFT, NVDA, SPY...</div>
          <div style={{ padding: '9px 22px', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', color: 'var(--muted)' }}>Scan →</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {DEMO_RESULTS.map((r, i) => (
          <div key={r.ticker} style={{
            background: STRENGTH_BG[r.strength],
            border: `1px solid ${r.strength === 'strong' ? 'rgba(133,196,124,.25)' : r.strength === 'moderate' ? 'rgba(212,168,67,.2)' : 'var(--edge)'}`,
            borderRadius: 3, padding: '12px 14px',
            filter: i >= 2 ? 'blur(3px)' : 'none',
            opacity: i >= 2 ? 0.45 : 1,
            pointerEvents: 'none', userSelect: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 130, flexShrink: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: STRENGTH_COLOR[r.strength], flexShrink: 0 }} />
                <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem' }}>{r.ticker}</span>
                {(r as any).type && (r as any).type !== 'stock' && (
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: (r as any).type === 'crypto' ? '#f7931a' : '#7eb8f7',
                    background: (r as any).type === 'crypto' ? 'rgba(247,147,26,.1)' : 'rgba(126,184,247,.1)',
                    border: `1px solid ${(r as any).type === 'crypto' ? 'rgba(247,147,26,.3)' : 'rgba(126,184,247,.3)'}`,
                    borderRadius: 2, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '.06em',
                  }}>{(r as any).type}</span>
                )}
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: STRENGTH_COLOR[r.strength], textTransform: 'uppercase' }}>{r.strength}</span>
              </div>
              <div style={{ minWidth: 120, flexShrink: 0 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: r.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>${r.price.toFixed(2)}</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: r.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>{r.change >= 0 ? '+' : ''}{r.change.toFixed(2)} ({r.changePct})</div>
              </div>
              <div style={{ flex: 1, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                  <span style={{ color: r.zone === 'discount' ? 'var(--cash)' : 'var(--red)', textTransform: 'uppercase', fontWeight: 700 }}>{r.zone}</span>
                  {' · EQ '}<span style={{ color: 'var(--text)' }}>${r.eq}</span>
                </div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>PDH <span style={{ color: 'var(--text)' }}>${r.pdh}</span></div>
                {r.fvg && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--amber)' }}>FVG ▲ ${r.fvg.bottom}–${r.fvg.top}</div>}
                {r.ob  && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)' }}>OB ▲ ${r.ob.bottom}–${r.ob.top}</div>}
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .6 }}>{r.signals} signal{r.signals !== 1 ? 's' : ''}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '22px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.88rem', color: 'var(--bill)', marginBottom: 8 }}>Scan any list of tickers</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', marginBottom: 16, lineHeight: 1.7 }}>
          Paste up to 20 tickers and instantly see which ones are near key ICT levels — FVGs, order blocks, PDH/PDL, premium and discount zones.
        </div>
        <button onClick={onUpgrade} style={{ padding: '10px 28px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.8rem', cursor: 'pointer' }}>
          Get Pro — $9.99/mo →
        </button>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginTop: 8, opacity: .6 }}>Cancel anytime</div>
      </div>
    </div>
  )
}

// ─── Inline trade panel for surge rows ────────────────────────────────────────
function SurgeTrade({ ticker, price, onClose }: { ticker: string; price: number; onClose: () => void }) {
  const [side,      setSide]      = useState<'buy'|'sell'>('buy')
  const [qty,       setQty]       = useState('')
  const [orderType, setOrderType] = useState<'market'|'limit'>('market')
  const [entry,     setEntry]     = useState(price > 0 ? price.toFixed(2) : '')
  const [stop,      setStop]      = useState('')
  const [target,    setTarget]    = useState('')
  const [error,     setError]     = useState('')
  const [success,   setSuccess]   = useState('')

  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (order: any) => api.trading.placeOrder(order, 'paper'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['positions'] })
      qc.invalidateQueries({ queryKey: ['my-trades'] })
      setSuccess('✓ Order placed in Alpaca paper account')
    },
    onError: (e: any) => setError(e.message ?? 'Order failed'),
  })

  function submit() {
    setError(''); setSuccess('')
    if (!qty || parseFloat(qty) <= 0) { setError('Enter number of shares'); return }
    const order: any = { symbol: ticker, qty: parseFloat(qty), side, type: orderType, time_in_force: 'gtc' }
    if (orderType === 'limit' && entry) order.limit_price = parseFloat(entry)
    if (stop)   order.stop_loss_price   = parseFloat(stop)
    if (target) order.take_profit_price = parseFloat(target)
    mutation.mutate(order)
  }

  const inp = (val: string, set: (v: string) => void, ph: string, label: string) => (
    <div>
      <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 }}>{label}</div>
      <input value={val} onChange={e => set(e.target.value)} placeholder={ph} type="number" step="0.01"
        style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '6px 8px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none', boxSizing: 'border-box' as const }} />
    </div>
  )

  return (
    <div style={{ borderTop: '1px solid var(--edge)', paddingTop: 12, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', display:'flex', alignItems:'center', gap:4 }}><img src="/icons/trading.svg" alt="" style={{width:11,height:11,opacity:.6}}/> Paper order · {ticker}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '1rem', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
      </div>

      {/* Buy / Sell */}
      <div style={{ display: 'flex', gap: 6 }}>
        {(['buy','sell'] as const).map(s => (
          <button key={s} onClick={() => setSide(s)} style={{
            flex: 1, padding: '6px', border: `1px solid ${side === s ? (s === 'buy' ? 'var(--cash)' : 'var(--red)') : 'var(--edge)'}`,
            background: side === s ? (s === 'buy' ? 'rgba(133,196,124,.1)' : 'rgba(224,92,92,.1)') : 'transparent',
            color: side === s ? (s === 'buy' ? 'var(--cash)' : 'var(--red)') : 'var(--muted)',
            fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', borderRadius: 2, cursor: 'pointer', textTransform: 'uppercase' as const,
          }}>{s}</button>
        ))}
      </div>

      {/* Shares + order type */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {inp(qty, setQty, '10', 'Shares')}
        <div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 }}>Order Type</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['market','limit'] as const).map(t => (
              <button key={t} onClick={() => setOrderType(t)} style={{
                flex: 1, padding: '6px 4px', border: `1px solid ${orderType === t ? 'var(--cash-dim)' : 'var(--edge)'}`,
                background: orderType === t ? 'var(--stamp)' : 'transparent',
                color: orderType === t ? 'var(--cash)' : 'var(--muted)',
                fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2, cursor: 'pointer', textTransform: 'uppercase' as const,
              }}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Price levels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {orderType === 'limit' && inp(entry, setEntry, price.toFixed(2), 'Limit Price')}
        {inp(stop,   setStop,   '',   'Stop Loss')}
        {inp(target, setTarget, '',   'Take Profit')}
        {orderType === 'market' && <div />}
      </div>

      {error   && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)' }}>✗ {error}</div>}
      {success && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--cash)' }}>{success}</div>}

      <button onClick={submit} disabled={mutation.isPending || !!success} style={{
        padding: '9px', background: side === 'buy' ? 'var(--cash)' : 'var(--red)',
        color: 'var(--ink)', border: 'none', borderRadius: 2,
        fontFamily: 'Syne', fontWeight: 800, fontSize: '.76rem', cursor: 'pointer',
        opacity: mutation.isPending || !!success ? .5 : 1,
      }}>
        {mutation.isPending ? 'Placing...' : `Place ${side.toUpperCase()} Order →`}
      </button>
    </div>
  )
}

// ─── Volume Surge row ─────────────────────────────────────────────────────────
function SurgeRow({ r, onAnalyze, isEod }: { r: any; onAnalyze: (ticker: string) => void; isEod?: boolean }) {
  const [tradeOpen, setTradeOpen] = useState(false)
  const [wlAdded,   setWlAdded]   = useState(false)
  const qc = useQueryClient()
  const wlMutation = useMutation({
    mutationFn: () => api.watchlist.add(r.ticker),
    onSuccess:  () => { setWlAdded(true); qc.invalidateQueries({ queryKey: ['watchlist'] }) },
  })
  const isHot = r.surgeRatio >= 3
  const isWarm = r.surgeRatio >= 1.5
  const color = isHot ? 'var(--cash)' : isWarm ? 'var(--amber)' : 'var(--muted)'
  const bg    = isHot ? 'rgba(133,196,124,.06)' : isWarm ? 'rgba(212,168,67,.04)' : 'transparent'

  return (
    <div style={{
      background: bg,
      border: `1px solid ${isHot ? 'rgba(133,196,124,.2)' : isWarm ? 'rgba(212,168,67,.15)' : 'var(--edge)'}`,
      borderRadius: 3, padding: '11px 14px',
    }}>
      <div className="surge-row-inner" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

        {/* Surge badge + ticker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140, flexShrink: 0 }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color, padding: '2px 6px', border: `1px solid ${color}`, borderRadius: 2, background: isHot ? 'rgba(133,196,124,.1)' : isWarm ? 'rgba(212,168,67,.08)' : 'transparent', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
            ▲ {r.surgeRatio}x{r.baselineLabel ? <span style={{ opacity: .6, marginLeft: 4, fontSize: '.48rem' }}>{r.baselineLabel}</span> : null}
          </div>
          <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem' }}>{r.ticker}</span>
        </div>

        {/* Price + change + float */}
        <div style={{ minWidth: 130, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: 'var(--cash)' }}>${r.price.toFixed(2)}</div>
            {r.isBullishBar && <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)', opacity: .7 }}>▲ bar</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)' }}>{r.changePct}</span>
            {r.floatM != null && (
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--amber)', padding: '1px 5px', border: '1px solid rgba(212,168,67,.3)', borderRadius: 2 }}>
                {r.floatM}M float
              </span>
            )}
            {r.spread != null && (
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: r.spread <= 0.02 ? 'var(--cash)' : r.spread <= 0.05 ? 'var(--amber)' : 'var(--red)', padding: '1px 5px', border: `1px solid ${r.spread <= 0.02 ? 'rgba(133,196,124,.3)' : r.spread <= 0.05 ? 'rgba(212,168,67,.3)' : 'rgba(220,80,80,.3)'}`, borderRadius: 2 }}>
                {(r.spread * 100).toFixed(1)}¢ spread
              </span>
            )}
          </div>
        </div>

        {/* Volume data */}
        <div style={{ flex: 1, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
            {isEod ? 'day vol' : '1m vol'} <span style={{ color }}>{r.lastMinVol.toLocaleString()}</span>
          </div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
            {isEod ? 'prev day' : 'prior 15m'} <span style={{ color: 'var(--text)' }}>{r.avg15mVol.toLocaleString()}</span>
          </div>
          {!isEod && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
            day vol <span style={{ color: 'var(--text)' }}>{r.dayVolume.toLocaleString()}</span>
          </div>}
          {r.vsYesterday !== 0 && (
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: r.vsYesterday > 0 ? 'var(--cash)' : 'var(--red)' }}>
              vs yday {r.vsYesterday > 0 ? '+' : ''}{r.vsYesterday}%
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => onAnalyze(r.ticker)}
            style={{ padding: '5px 12px', background: 'var(--stamp)', border: '1px solid var(--cash-dim)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: 'pointer' }}>
            Analyze →
          </button>
          <button
            onClick={() => !wlAdded && wlMutation.mutate()}
            disabled={wlAdded || wlMutation.isPending}
            style={{ padding: '5px 12px', background: wlAdded ? 'var(--stamp)' : 'transparent', border: `1px solid ${wlAdded ? 'var(--cash-dim)' : 'var(--edge)'}`, borderRadius: 2, color: wlAdded ? 'var(--cash)' : 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: wlAdded ? 'default' : 'pointer', transition: 'all .15s' }}>
            {wlAdded ? '✓ Watchlist' : wlMutation.isPending ? '...' : '+ Watch'}
          </button>
          <button
            onClick={() => setTradeOpen(o => !o)}
            style={{ padding: '5px 12px', background: tradeOpen ? 'rgba(133,196,124,.15)' : 'var(--stamp)', border: `1px solid ${tradeOpen ? 'var(--cash)' : 'var(--edge)'}`, borderRadius: 2, color: tradeOpen ? 'var(--cash)' : 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: 'pointer' }}>
            Trade {tradeOpen ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {tradeOpen && (
        <SurgeTrade ticker={r.ticker} price={r.price} onClose={() => setTradeOpen(false)} />
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Scanner() {
  const navigate  = useNavigate()
  const { user }  = useAuthStore()
  const isFree    = !user || user.tier === 'free'
  const isSuper   = user?.tier === 'super'

  const [scanTab, setScanTab] = useState<'ict'|'surge-a'|'surge-b'|'surge-c'|'surge-d'|'calls'|'sharp'|'auto'>('ict')
  const [input,   setInput]   = useState('')
  const [results, setResults] = useState<any[]>([])
  const [scannedAt, setScannedAt] = useState('')
  const [surgeResults, setSurgeResults] = useState<any[]>([])
  const [surgeAt, setSurgeAt]           = useState('')
  const [surgeTotal, setSurgeTotal]     = useState(0)
  const [surgeScanned, setSurgeScanned] = useState(0)
  const [surgeIsEod,     setSurgeIsEod]      = useState(false)
  const [surgeIsPremarket, setSurgeIsPremarket] = useState(false)
  const [surgeWaiting,   setSurgeWaiting]    = useState(false)
  const [surgeMinsAfterOpen, setSurgeMinsAfterOpen] = useState<number|null>(null)
  const [floatMax,      setFloatMax]      = useState<20|50|100|0>(50)
  const [minChangePct,  setMinChangePct]  = useState(2)
  const [minSurgeRatio,  setMinSurgeRatio]  = useState(2)
  const [maxSpreadCents, setMaxSpreadCents] = useState<number|null>(null)
  const [minDailyVolume, setMinDailyVolume] = useState<number>(500000)
  const [surgeMode,     setSurgeMode]     = useState<'A'|'B'|'C'|'D'>('D')
  const paramsRef = useRef({ floatMax, minChangePct, minSurgeRatio, maxSpreadCents, minDailyVolume, surgeMode })
  const [cooldown, setCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const scanMutation = useMutation({
    mutationFn: (tickers: string[]) => api.scanner.scan(tickers),
    onSuccess: (data) => { setResults(data.results); setScannedAt(data.scannedAt) },
  })

  const surgeMutation = useMutation({
    mutationFn: (p: { floatMax: 20|50|100|0; minChangePct: number; minSurgeRatio: number; maxSpreadCents: number|null; minDailyVolume: number; surgeMode: 'A'|'B'|'C'|'D' }) => api.scanner.volumeSurge(p),
    onSuccess: (data) => {
      setSurgeResults(data.results ?? [])
      setSurgeAt(data.scannedAt)
      setSurgeTotal(data.total ?? 0)
      setSurgeScanned(data.scanned ?? 0)
      setSurgeIsEod(data.isEod ?? false)
      setSurgeIsPremarket(data.isPremarket ?? false)
      setSurgeWaiting(data.waitingForOpen ?? false)
      setSurgeMinsAfterOpen(data.minsAfterOpen ?? null)
    },
  })

  // Keep paramsRef in sync
  useEffect(() => { paramsRef.current = { floatMax, minChangePct, minSurgeRatio, maxSpreadCents, minDailyVolume, surgeMode } }, [floatMax, minChangePct, minSurgeRatio, maxSpreadCents, minDailyVolume, surgeMode])

  const roboActiveRef = useRef(false)
  const [roboActive, setRoboActive] = useState(false)
  const [roboStatus, setRoboStatus] = useState('')
  const [roboTrades, setRoboTrades] = useState<any[]>([])
  const roboIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [roboScanCount, setRoboScanCount] = useState(0)
  const [roboNextScan, setRoboNextScan] = useState(0)
  const roboCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // keep ref in sync so useEffect closure sees latest value
  useEffect(() => { roboActiveRef.current = roboActive }, [roboActive])

  // Mr. Roboto toggle — starts/stops 60s auto-scan loop
  const toggleRobo = async () => {
    if (roboActive) {
      // Deactivating — close positions + stop loop
      setRoboStatus('🤖 Deactivating — closing all positions...')
      try {
        const res = await api.robotrader.deactivate()
        setRoboStatus(`🤖 ${res.message ?? 'Deactivated'}`)
      } catch (e: any) {
        setRoboStatus(`🤖 Deactivated (error: ${e.message})`)
      }
      setRoboActive(false)
      if (roboIntervalRef.current) { clearInterval(roboIntervalRef.current); roboIntervalRef.current = null }
      if (roboCountdownRef.current) { clearInterval(roboCountdownRef.current); roboCountdownRef.current = null }
      setRoboNextScan(0)
    } else {
      // Activating — start auto-scan loop
      setRoboActive(true)
      setRoboStatus('🤖 ALGO ACTIVE — scanning every 60s')
      setRoboTrades([])
      setRoboScanCount(0)

      // Run first scan immediately
      runRoboScan()

      // 60-second countdown timer
      setRoboNextScan(60)
      if (roboCountdownRef.current) clearInterval(roboCountdownRef.current)
      roboCountdownRef.current = setInterval(() => {
        setRoboNextScan(prev => prev <= 1 ? 60 : prev - 1)
      }, 1000)

      // 60-second scan loop
      if (roboIntervalRef.current) clearInterval(roboIntervalRef.current)
      roboIntervalRef.current = setInterval(() => {
        if (roboActiveRef.current) runRoboScan()
      }, 60_000)
    }
  }

  // Run a single robo scan cycle
  const runRoboScan = () => {
    setRoboScanCount(prev => prev + 1)
    // Trigger the volume surge scan. Read filters from paramsRef (updated every render) NOT the closure —
    // otherwise changing filters / Surge mode while Roboto is active had no effect on auto-scans. Code review P1.
    setSurgeResults([])
    surgeMutation.mutate({ ...paramsRef.current })
    // Set the cooldown to match
    setCooldown(60)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (roboIntervalRef.current) clearInterval(roboIntervalRef.current)
      if (roboCountdownRef.current) clearInterval(roboCountdownRef.current)
      if (cooldownRef.current) clearInterval(cooldownRef.current)   // was leaking a 1s interval per visit. Code review P2.
    }
  }, [])

  const runSurgeScan = () => {
    setSurgeResults([])
    setRoboStatus('')
    setRoboTrades([])
    surgeMutation.mutate({ floatMax, minChangePct, minSurgeRatio, maxSpreadCents, minDailyVolume, surgeMode })
    // 60s cooldown — prevent hammering the API
    setCooldown(60)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // Fire robo trades after scan completes (only when robo is active)
  useEffect(() => {
    if (!roboActiveRef.current || surgeResults.length === 0) return
    const payload = surgeResults.slice(0, 20).map((r: any) => ({
      ticker:       r.ticker,
      price:        r.price,
      surgeRatio:   r.surgeRatio,
      changePctNum: r.changePctNum ?? 0,
    }))
    setRoboStatus('🤖 Placing trades...')
    api.robotrader.execute(payload)
      .then((res: any) => {
        setRoboTrades(res.tradesPlaced ?? [])
        setRoboStatus(res.message ?? `${(res.tradesPlaced ?? []).length} trade(s) placed`)
      })
      .catch((e: any) => setRoboStatus(`🤖 Error: ${e.message}`))
  }, [surgeResults])

  // ─── DJT Intelligence state ─────────────────────────────────────────────────
  const [djtSignal, setDjtSignal] = useState<any>(null)

  // Poll DJT signal every 60s (Super users only)
  useEffect(() => {
    if (!isSuper) return
    const fetchDjt = () => {
      api.djt.signal()
        .then((res: any) => {
          if (res) setDjtSignal(res)
        })
        .catch(() => {})
    }
    fetchDjt()
    const interval = setInterval(fetchDjt, 60_000)
    return () => clearInterval(interval)
  }, [isSuper])

  function scan(tickers?: string[]) {
    const list = tickers ?? input.split(/[\s,]+/).map(t => t.trim().toUpperCase()).filter(Boolean)
    if (!list.length) return
    if (!tickers) setInput(list.join(', '))
    setResults([])
    scanMutation.mutate(list)
  }

  function goAnalyze(ticker: string) {
    navigate('/analyze', { state: { ticker } })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Setup Scanner</h1>
          <div className="eyebrow">ICT key levels {isSuper ? '· Volume surge · NASDAQ-wide' : ''}{isFree ? ' · Preview mode' : ''}</div>
        </div>

        {/* Scanner type tabs — only for paid users. Grouped: Options Calls (primary) | Stocks scanners */}
        {!isFree && (() => {
          const tabBtn = (active: boolean, locked: boolean, onClick: () => void, top: any, desc: string, first = false) => (
            <button onClick={onClick} disabled={locked} style={{
              padding: '7px 12px', background: active ? 'var(--stamp)' : 'transparent',
              border: 'none', borderLeft: first ? 'none' : '1px solid var(--edge)',
              borderBottom: active ? '2px solid var(--cash)' : '2px solid transparent',
              color: active ? 'var(--cash)' : 'var(--muted)',
              fontFamily: 'Syne', fontWeight: 600, fontSize: '0.72rem',
              cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, whiteSpace: 'nowrap',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{top}{locked && ' 🔒'}</span>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', opacity: .6 }}>{desc}</span>
            </button>
          )
          const groupWrap: any = { display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden', flexWrap: 'wrap' }
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
              {/* ── Options · Calls (primary) ── */}
              <div>
                <div className="eyebrow" style={{ marginBottom: 5, fontSize: '.46rem', letterSpacing: '.1em' }}>Options · Calls</div>
                <div className="scanner-tabs" style={groupWrap}>
                  {tabBtn(scanTab === 'sharp', !isSuper, () => { if (isSuper) setScanTab('sharp') },
                    <><span>🎯</span> Calls V2_Beta</>, 'Best overall · ranked', true)}
                  {tabBtn(scanTab === 'calls', !isSuper, () => { if (isSuper) setScanTab('calls') },
                    <><span>📈</span> Call Scanner</>, 'By risk tier')}
                  {tabBtn(scanTab === 'auto', !isSuper, () => { if (isSuper) setScanTab('auto') },
                    <><span>🤖</span> Auto Calls</>, 'Self-trading · paper')}
                </div>
              </div>

              {/* ── Stocks · Levels & Momentum ── */}
              <div>
                <div className="eyebrow" style={{ marginBottom: 5, fontSize: '.46rem', letterSpacing: '.1em' }}>Stocks · Levels &amp; Momentum</div>
                <div className="scanner-tabs" style={groupWrap}>
                  {tabBtn(scanTab === 'ict', false, () => setScanTab('ict'),
                    <><img src="/icons/market_structure.svg" alt="" style={{ width: 13, height: 13, verticalAlign: 'middle' }} /> ICT Levels</>, 'Key levels', true)}
                  {([
                    { tab: 'surge-a', label: 'Surge A', desc: 'At the open' },
                    { tab: 'surge-b', label: 'Surge B', desc: 'Mid-day · 9:40+' },
                    { tab: 'surge-c', label: 'Surge C', desc: 'Premarket' },
                    { tab: 'surge-d', label: 'Surge D', desc: 'All day · default' },
                  ] as const).map(({ tab, label, desc }) => (
                    <span key={tab} style={{ display: 'contents' }}>{tabBtn(scanTab === tab, !isSuper, () => {
                      if (!isSuper) return
                      setScanTab(tab)
                      setSurgeMode(tab.split('-')[1].toUpperCase() as 'A'|'B'|'C'|'D')
                    }, <><img src={NAV_ICONS.volume_surge} alt="" style={{ width: 12, height: 12, objectFit: 'contain' }} /> {label}</>, desc)}</span>
                  ))}
                </div>
              </div>
            </div>
          )
        })()}
      </div>

      {/* Free preview */}
      {isFree && <FreePreview onUpgrade={() => navigate('/pricing')} />}

      {/* Cheap-calls screener */}
      {!isFree && scanTab === 'calls' && isSuper && <CallsScanner />}

      {/* Sharp-calls screener (v2 — relative-value + momentum ranked) */}
      {!isFree && scanTab === 'sharp' && isSuper && <SharpCallsScanner />}

      {/* Auto Calls — the scanner trading its own picks on paper */}
      {!isFree && scanTab === 'auto' && isSuper && <AutoCallsPanel />}

      {/* ── ICT Scanner tab ── */}
      {!isFree && scanTab === 'ict' && (
        <>
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px', marginBottom: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Tickers to scan — stocks · crypto (BTC, ETH, SOL) · futures (ES1!, NQ1!, CL1!)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={input}
                onChange={e => setInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && scan()}
                placeholder="AAPL MSFT BTC ETH ES1! NQ1! SOL..."
                style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 11px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.75rem', outline: 'none' }}
              />
              <button onClick={() => scan()} disabled={!input.trim() || scanMutation.isPending}
                style={{ padding: '9px 22px', background: input.trim() ? 'var(--cash)' : 'var(--paper)', color: input.trim() ? 'var(--ink)' : 'var(--muted)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', cursor: input.trim() ? 'pointer' : 'default' }}>
                {scanMutation.isPending ? 'Scanning...' : 'Scan →'}
              </button>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', marginRight: 4 }}>Presets:</span>
              {Object.entries(PRESETS).map(([label, tickers]) => (
                <button key={label} onClick={() => scan(tickers)}
                  style={{ padding: '4px 11px', background: 'transparent', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.58rem', cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            {Object.entries(STRENGTH_COLOR).map(([k, color]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />{k}
              </div>
            ))}
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', marginLeft: 'auto' }}>
              {scannedAt ? `Scanned ${new Date(scannedAt).toLocaleTimeString()}` : 'Ranked by signal strength'}
            </span>
          </div>

          {scanMutation.isPending && (
            <div style={{ padding: '36px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)', marginBottom: 8 }}>Fetching quotes and daily bars...</div>
            </div>
          )}

          {results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {results.map((r: any, i: number) => {
                const strength = r.levels?.signalStrength ?? 'none'
                const q  = r.quote
                const lv = r.levels
                return (
                  <div key={r.ticker + i} style={{
                    background: STRENGTH_BG[strength] ?? 'transparent',
                    border: `1px solid ${strength === 'strong' ? 'rgba(133,196,124,.25)' : strength === 'moderate' ? 'rgba(212,168,67,.2)' : 'var(--edge)'}`,
                    borderRadius: 3, padding: '12px 14px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 130, flexShrink: 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: STRENGTH_COLOR[strength], flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem' }}>{r.ticker}</span>
                        <span style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: STRENGTH_COLOR[strength], textTransform: 'uppercase' }}>{strength}</span>
                      </div>
                      {q ? (
                        <div style={{ minWidth: 120, flexShrink: 0 }}>
                          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: q.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>${q.price?.toFixed(2)}</div>
                          <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: q.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>{q.change >= 0 ? '+' : ''}{q.change?.toFixed(2)} ({q.changePct})</div>
                        </div>
                      ) : (
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--red)', minWidth: 120 }}>{r.error ?? 'No data'}</div>
                      )}
                      {lv && (
                        <div style={{ flex: 1, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                            <span style={{ color: lv.zone === 'discount' ? 'var(--cash)' : 'var(--red)', textTransform: 'uppercase', fontWeight: 700 }}>{lv.zone}</span>
                            {' · EQ '}<span style={{ color: 'var(--text)' }}>${lv.equilbrium}</span>
                          </div>
                          {lv.pdh && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>PDH <span style={{ color: 'var(--text)' }}>${lv.pdh}</span></div>}
                          {lv.pdl && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>PDL <span style={{ color: 'var(--text)' }}>${lv.pdl}</span></div>}
                          {lv.nearFVGs?.length > 0 && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--amber)' }}>FVG {lv.nearFVGs[0].type === 'bull' ? '▲' : '▼'} ${lv.nearFVGs[0].bottom}–${lv.nearFVGs[0].top}</div>}
                          {lv.nearOBs?.length  > 0 && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)' }}>OB {lv.nearOBs[0].type === 'bull' ? '▲' : '▼'} ${lv.nearOBs[0].bottom}–${lv.nearOBs[0].top}</div>}
                          <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .6 }}>{lv.signalCount} signal{lv.signalCount !== 1 ? 's' : ''}</div>
                        </div>
                      )}
                      <button onClick={() => navigate('/analyze', { state: { ticker: r.ticker } })}
                        style={{ padding: '5px 12px', background: 'var(--stamp)', border: '1px solid var(--cash-dim)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: 'pointer', flexShrink: 0 }}>
                        Analyze →
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* ── Volume Surge tabs ── */}
      {!isFree && scanTab.startsWith('surge-') && isSuper && (
        <>
          {/* Header + description banner */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <img src={NAV_ICONS.volume_surge} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  Volume Surge Scanner
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--cash)', marginLeft: 4 }}>
                    {scanTab === 'surge-a' && '· Mode A'}
                    {scanTab === 'surge-b' && '· Mode B'}
                    {scanTab === 'surge-c' && '· Mode C'}
                    {scanTab === 'surge-d' && '· Mode D'}
                  </span>
                </div>

                {/* Per-tab description */}
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', marginBottom: 14, padding: '8px 12px', background: 'var(--paper)', borderRadius: 2, borderLeft: '2px solid var(--cash-dim)' }}>
                  {scanTab === 'surge-a' && '↑ Mode A — Compares last 1-min bar vs daily avg ÷ 390. Best for open — eliminates premarket baseline contamination. Most professional scanners use this method.'}
                  {scanTab === 'surge-b' && '⏳ Mode B — Skips first 10 min after open. No chaotic open noise. Uses standard 15-bar rolling avg. Returns nothing before 9:40am ET — use another tab until then.'}
                  {scanTab === 'surge-c' && '📊 Mode C — Uses 80th percentile of last 3hr of 1-min bars as threshold. Best for true premarket momentum — ignores dead-volume spikes from thin premarket activity.'}
                  {scanTab === 'surge-d' && '🔄 Mode D — Session-aware: premarket → 30-bar avg, first 10min after open → daily avg baseline, regular session → 15-bar rolling avg. Recommended default.'}
                </div>

                <div className="filter-row" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>

                  {/* Float filter */}
                  <div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginBottom: 5 }}>MAX FLOAT</div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {([0, 100, 50, 20] as const).map(fm => (
                        <button key={fm} onClick={() => { setFloatMax(fm); if (surgeAt) { setSurgeResults([]); surgeMutation.mutate({ floatMax: fm as 20|50|100|0, minChangePct: paramsRef.current.minChangePct, minSurgeRatio: paramsRef.current.minSurgeRatio, maxSpreadCents: paramsRef.current.maxSpreadCents, minDailyVolume: paramsRef.current.minDailyVolume, surgeMode: paramsRef.current.surgeMode }) } }} style={{
                          padding: '5px 12px',
                          background: floatMax === fm ? 'var(--cash)' : 'transparent',
                          color:      floatMax === fm ? 'var(--ink)' : 'var(--muted)',
                          border:     `1px solid ${floatMax === fm ? 'var(--cash)' : 'var(--edge)'}`,
                          borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                        }}>{fm === 0 ? 'No Cap' : `<${fm}M`}</button>
                      ))}
                    </div>
                  </div>

                  {/* Min % gain */}
                  <div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginBottom: 5 }}>MIN GAIN</div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {([2, 5] as const).map(pct => (
                        <button key={pct} onClick={() => { setMinChangePct(pct); if (surgeAt) { setSurgeResults([]); surgeMutation.mutate({ floatMax, minChangePct: pct, minSurgeRatio, maxSpreadCents, minDailyVolume, surgeMode }) } }} style={{
                          padding: '5px 10px',
                          background: minChangePct === pct ? 'var(--cash)' : 'transparent',
                          color:      minChangePct === pct ? 'var(--ink)' : 'var(--muted)',
                          border:     `1px solid ${minChangePct === pct ? 'var(--cash)' : 'var(--edge)'}`,
                          borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                        }}>{`>${pct}%`}</button>
                      ))}
                    </div>
                  </div>

                  {/* Min surge ratio */}
                  <div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginBottom: 5 }}>MIN SURGE</div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {([2, 3, 5] as const).map(r => (
                        <button key={r} onClick={() => setMinSurgeRatio(r)} style={{
                          padding: '5px 10px',
                          background: minSurgeRatio === r ? 'var(--cash)' : 'transparent',
                          color:      minSurgeRatio === r ? 'var(--ink)' : 'var(--muted)',
                          border:     `1px solid ${minSurgeRatio === r ? 'var(--cash)' : 'var(--edge)'}`,
                          borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                        }}>{r}×+</button>
                      ))}
                    </div>
                  </div>

                  {/* Spread filter */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', minWidth: 80 }}>MAX SPREAD</span>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {([null, 1, 2, 5] as const).map(v => (
                        <button key={String(v)} onClick={() => setMaxSpreadCents(v)} style={{
                          padding: '5px 10px',
                          background: maxSpreadCents === v ? 'var(--cash)' : 'transparent',
                          color:      maxSpreadCents === v ? 'var(--ink)' : 'var(--muted)',
                          border:     `1px solid ${maxSpreadCents === v ? 'var(--cash)' : 'var(--edge)'}`,
                          borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                        }}>{v === null ? 'OFF' : `<${v}¢`}</button>
                      ))}
                    </div>
                  </div>

                  {/* Min daily volume filter */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', minWidth: 80 }}>MIN VOL</span>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {([0, 500000, 1000000, 5000000] as const).map(v => (
                        <button key={v} onClick={() => { setMinDailyVolume(v); if (surgeAt) { setSurgeResults([]); surgeMutation.mutate({ floatMax, minChangePct, minSurgeRatio, maxSpreadCents, minDailyVolume: v, surgeMode }) } }} style={{
                          padding: '5px 10px',
                          background: minDailyVolume === v ? 'var(--cash)' : 'transparent',
                          color:      minDailyVolume === v ? 'var(--ink)' : 'var(--muted)',
                          border:     `1px solid ${minDailyVolume === v ? 'var(--cash)' : 'var(--edge)'}`,
                          borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                        }}>{v === 0 ? 'OFF' : v >= 1000000 ? `${v/1000000}M+` : `${v/1000}K+`}</button>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
              <button
                onClick={toggleRobo}
                title="Mr. Roboto Algo Trader — auto-scans every 60s and places paper trades"
                style={{
                  padding: '10px 14px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
                  fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap',
                  background: roboActive ? 'rgba(133,196,124,.15)' : 'transparent',
                  color: roboActive ? 'var(--cash)' : 'var(--muted)',
                  border: `1px solid ${roboActive ? 'var(--cash)' : 'var(--edge)'}`,
                  transition: 'all .2s',
                }}>
                🤖 {roboActive ? `ALGO ON · ${roboNextScan}s` : 'MR. ROBOTO'}
              </button>
              <button
                onClick={runSurgeScan}
                disabled={surgeMutation.isPending || cooldown > 0}
                style={{
                  padding: '10px 24px', background: cooldown > 0 ? 'transparent' : 'var(--cash)',
                  color: cooldown > 0 ? 'var(--muted)' : 'var(--ink)',
                  border: cooldown > 0 ? '1px solid var(--edge)' : 'none',
                  borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
                  fontSize: '.78rem',
                  cursor: (surgeMutation.isPending || cooldown > 0) ? 'not-allowed' : 'pointer',
                  opacity: surgeMutation.isPending ? .5 : 1, whiteSpace: 'nowrap',
                  minWidth: 120, textAlign: 'center',
                }}>
                {surgeMutation.isPending ? 'Scanning...' : cooldown > 0 ? `Wait ${cooldown}s` : 'Run Scan →'}
              </button>
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {[['▲ 3x+ surge', 'var(--cash)'], ['▲ 1.5x+ surge', 'var(--amber)']].map(([label, color]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color as string }} />{label}
              </div>
            ))}
            {surgeAt && (
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', marginLeft: 'auto' }}>
                {surgeTotal} surges · {surgeScanned.toLocaleString()} tickers scanned · top 100 by vol
                <span style={{ marginLeft: 10, color: 'var(--cash)', opacity: .8 }}>
                  · last scan {new Date(surgeAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </span>
            )}

            {/* Mr. Roboto algo status */}
            {(roboStatus || roboActive) && (
              <div style={{ margin: '8px 0 0', padding: '8px 12px', background: roboTrades.length > 0 ? 'rgba(133,196,124,.08)' : roboActive ? 'rgba(133,196,124,.04)' : 'rgba(255,200,80,.06)', border: `1px solid ${roboTrades.length > 0 ? 'var(--cash-dim)' : roboActive ? 'rgba(133,196,124,.15)' : 'rgba(255,200,80,.2)'}`, borderRadius: 3, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
                <span style={{ color: roboTrades.length > 0 ? 'var(--cash)' : roboActive ? 'var(--cash)' : 'var(--amber)' }}>
                  🤖 {roboActive ? `ALGO ACTIVE · Scan #${roboScanCount} · Next in ${roboNextScan}s` : roboStatus}
                </span>
                {roboTrades.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {roboTrades.map((t: any) => (
                      <span key={t.orderId} style={{ background: 'rgba(133,196,124,.1)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '3px 8px', fontSize: '.58rem' }}>
                        {t.ticker} · ${t.notional} · SL {t.stopLoss?.toFixed(2)} · TP {t.takeProfit?.toFixed(2)}
                        {t.aiFiltered && t.aiConfidence ? <span style={{ color: 'var(--amber)', marginLeft: 4 }}>🧠 {t.aiConfidence}%</span> : null}
                        {t.aiReason ? <span style={{ color: 'var(--muted)', marginLeft: 4 }}>— {t.aiReason}</span> : null}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Loading */}
          {surgeMutation.isPending && (
            <div style={{ padding: '48px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 160, height: 2, background: 'var(--edge)', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,var(--cash),transparent)', animation: 'sweep 1.4s ease-in-out infinite' }} />
              </div>
              <style>{`@keyframes sweep{0%{transform:translateX(-200%)}100%{transform:translateX(400%)}}`}</style>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)' }}>Scanning all US tickers for volume surges...</div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .5 }}>This takes 5–10 seconds</div>
            </div>
          )}

          {/* Error */}
          {surgeMutation.isError && (
            <div style={{ background: 'rgba(224,92,92,.06)', border: '1px solid rgba(224,92,92,.25)', borderRadius: 3, padding: 14 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--red)' }}>
                Scan failed: {(surgeMutation.error as any)?.message ?? 'Unknown error'}
              </div>
            </div>
          )}

          {/* Session mode banner */}
          {surgeAt && surgeIsPremarket && (
            <div style={{ background: 'rgba(133,196,124,.05)', border: '1px solid rgba(133,196,124,.2)', borderRadius: 3, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/icons/dca_clock.svg" alt="" style={{width:14,height:14,verticalAlign:'middle'}}/>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--cash)', lineHeight: 1.6 }}>
                <strong>Pre-market</strong> — showing live pre-market data (4am–9:30am ET). Volume and price reflect pre-market activity against previous close.
              </div>
            </div>
          )}
          {surgeAt && surgeIsEod && (
            <div style={{ background: 'rgba(212,168,67,.06)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 3, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/icons/dca_clock.svg" alt="" style={{width:14,height:14,verticalAlign:'middle',opacity:.5}}/>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', lineHeight: 1.6 }}>
                <strong>Market closed</strong> — showing last trading day's data. Surge ratio = today vs previous day volume. Bullish bar = day closed above open.
              </div>
            </div>
          )}

          {/* No results yet */}
          {!surgeMutation.isPending && surgeResults.length === 0 && surgeAt === '' && (
            <div style={{ padding: '48px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', opacity: .5 }}>
              Click "Run Scan" to find volume surges across all US stocks
            </div>
          )}

          {/* Mode B waiting for open */}
          {!surgeMutation.isPending && surgeWaiting && (
            <div style={{ padding: '36px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--amber)' }}>
              ⏳ Mode B: Waiting for 9:40am ET to avoid open noise<br />
              <span style={{ opacity: .7, fontSize: '.58rem' }}>
                {surgeMinsAfterOpen !== null ? `${surgeMinsAfterOpen.toFixed(1)} min into session — check back in ${(10 - surgeMinsAfterOpen).toFixed(0)} min` : 'Market just opened — waiting for 10-minute buffer'}
              </span>
            </div>
          )}

          {/* No surges found */}
          {!surgeMutation.isPending && surgeResults.length === 0 && surgeAt !== '' && !surgeWaiting && (
            <div style={{ padding: '36px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>
              No surges found right now — market may be closed or low volume session.<br />
              <span style={{ opacity: .6, fontSize: '.58rem' }}>Best results during NYSE hours (9:30am–4pm ET)</span>
            </div>
          )}

          {/* Results */}
          {surgeResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {surgeResults.map((r: any) => (
                <SurgeRow key={r.ticker} r={r} onAnalyze={goAnalyze} isEod={surgeIsEod} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Super upsell on surge tab for Pro users */}
      {!isFree && scanTab.startsWith('surge-') && !isSuper && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '32px', textAlign: 'center' }}>
          <img src="/icons/scanner.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:12}}/>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: 'var(--bill)', marginBottom: 8 }}>Volume Surge Scanner</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', lineHeight: 1.8, marginBottom: 20, maxWidth: 420, margin: '0 auto 20px' }}>
            Scan the entire US market every minute for volume spikes.<br />
            Find stocks where the last 1-minute bar volume exceeds the 15-minute average — a classic early signal for institutional accumulation and momentum breakouts.<br /><br />
            Requires Super tier for live 1-minute data access.
          </div>
          <button onClick={() => navigate('/pricing')} style={{ padding: '10px 28px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.8rem', cursor: 'pointer' }}>
            Upgrade to Super — $99.99/mo →
          </button>
        </div>
      )}

      {/* ── DJT Intelligence Panel — Super only ── */}
      {isSuper && (
        <div style={{ marginTop: 24, background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: '1.2rem' }}>🏛️</span>
            <div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.85rem', color: 'var(--bill)' }}>DJT Intelligence</div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)' }}>Truth Social monitor · Claude AI classification · auto-trade signals</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span onClick={() => navigate('/djt')} style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--cash)', cursor: 'pointer' }}>Full feed →</span>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: djtSignal ? 'var(--cash)' : 'var(--edge)' }} />
            </div>
          </div>

          {/* Latest signal */}
          {djtSignal?.signal ? (
            <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3, padding: '12px 14px' }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 8, lineHeight: 1.7 }}>
                "{djtSignal.post?.excerpt?.slice(0, 200)}{(djtSignal.post?.excerpt?.length ?? 0) > 200 ? '...' : ''}"
              </div>
              <div className="djt-signals" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{
                  padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.7rem',
                  background: djtSignal.signal.direction === 'BUY' ? 'rgba(133,196,124,.12)' : djtSignal.signal.direction === 'SELL' ? 'rgba(224,92,92,.12)' : 'rgba(128,128,128,.08)',
                  color: djtSignal.signal.direction === 'BUY' ? 'var(--cash)' : djtSignal.signal.direction === 'SELL' ? 'var(--red)' : 'var(--muted)',
                  border: `1px solid ${djtSignal.signal.direction === 'BUY' ? 'var(--cash-dim)' : djtSignal.signal.direction === 'SELL' ? 'rgba(224,92,92,.3)' : 'var(--edge)'}`,
                }}>
                  {djtSignal.signal.direction === 'BUY' ? '▲' : djtSignal.signal.direction === 'SELL' ? '▼' : '—'} {djtSignal.signal.direction}
                </div>
                <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--bill)', border: '1px solid var(--edge)' }}>{djtSignal.signal.instrument}</div>
                <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', border: '1px solid rgba(212,168,67,.2)' }}>{djtSignal.signal.topic}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 2, border: '1px solid var(--edge)' }}>
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>STR</span>
                  <div style={{ width: 50, height: 4, background: 'var(--edge)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${(djtSignal.signal.strength ?? 0) * 10}%`, height: '100%', background: (djtSignal.signal.strength ?? 0) >= 7 ? 'var(--red)' : (djtSignal.signal.strength ?? 0) >= 4 ? 'var(--amber)' : 'var(--muted)', borderRadius: 2 }} />
                  </div>
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--text)' }}>{djtSignal.signal.strength}/10</span>
                </div>
                <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.6rem', color: djtSignal.signal.urgency === 'high' ? 'var(--red)' : 'var(--muted)', border: `1px solid ${djtSignal.signal.urgency === 'high' ? 'rgba(224,92,92,.3)' : 'var(--edge)'}` }}>{djtSignal.signal.urgency}</div>
                <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.6rem', color: djtSignal.signal.sentiment === 'bullish' ? 'var(--cash)' : djtSignal.signal.sentiment === 'bearish' ? 'var(--red)' : 'var(--muted)', border: '1px solid var(--edge)' }}>{djtSignal.signal.sentiment}</div>
              </div>
              {djtSignal.signal.reason && (
                <div style={{ marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', lineHeight: 1.6 }}>💡 {djtSignal.signal.reason}</div>
              )}
              <div style={{ marginTop: 6, fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', display: 'flex', gap: 10 }}>
                <span>{djtSignal.analyzedAt ? new Date(djtSignal.analyzedAt).toLocaleTimeString() : ''}</span>
                {djtSignal.tradesPlaced > 0 && <span style={{ color: 'var(--cash)' }}>🤖 {djtSignal.tradesPlaced} trade(s) placed</span>}
                {djtSignal.post?.url && <a href={djtSignal.post.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cash)', textDecoration: 'none' }}>View post ↗</a>}
              </div>
            </div>
          ) : djtSignal?.message ? (
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '12px 0' }}>
              Last check: {djtSignal.post?.excerpt ? `"${djtSignal.post.excerpt.slice(0, 80)}..." — ${djtSignal.message}` : djtSignal.message}
            </div>
          ) : (
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '16px 0', textAlign: 'center' }}>
              Monitoring Truth Social for market-moving posts — no signals yet today
            </div>
          )}
        </div>
      )}

      {/* Risk calc always shown for paid users on ICT tab */}
      {!isFree && scanTab === 'ict' && <RiskCalc />}
    </div>
  )
}

// ─── Risk Calculator ──────────────────────────────────────────────────────────
function RiskCalc() {
  const [account, setAccount] = useState('')
  const [riskPct, setRiskPct] = useState('1')
  const [entry,   setEntry]   = useState('')
  const [stop,    setStop]    = useState('')

  const calc = (() => {
    const acc  = parseFloat(account)
    const rpct = parseFloat(riskPct) / 100
    const ent  = parseFloat(entry)
    const stp  = parseFloat(stop)
    if (!acc || !rpct || !ent || !stp || ent === stp) return null
    const riskPerShare = Math.abs(ent - stp)
    const dollarRisk   = acc * rpct
    const shares       = Math.floor(dollarRisk / riskPerShare)
    const positionSize = shares * ent
    return { dollarRisk, shares, positionSize, riskPerShare }
  })()

  return (
    <div style={{ marginTop: 24, background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>Risk Calculator</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {([
          { label: 'Account Size', val: account, set: setAccount, ph: '50000',  prefix: '$' },
          { label: 'Risk %',       val: riskPct, set: setRiskPct, ph: '1',      prefix: '%' },
          { label: 'Entry Price',  val: entry,   set: setEntry,   ph: '187.50', prefix: '$' },
          { label: 'Stop Loss',    val: stop,    set: setStop,    ph: '184.80', prefix: '$' },
        ] as { label: string; val: string; set: (v: string) => void; ph: string; prefix: string }[]).map(({ label, val, set, ph, prefix }) => (
          <div key={label} style={{ flex: '1 1 100px' }}>
            <div className="eyebrow" style={{ marginBottom: 5 }}>{prefix} {label}</div>
            <input value={val} onChange={e => set(e.target.value)} placeholder={ph} type="number" step="any"
              style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        ))}
      </div>
      {calc && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: 8 }}>
          {[
            { label: 'Dollar Risk',   val: `$${calc.dollarRisk.toFixed(2)}`,        color: 'var(--red)' },
            { label: 'Shares',        val: `${calc.shares}`,                         color: 'var(--bill)' },
            { label: 'Position Size', val: `$${calc.positionSize.toLocaleString()}`, color: 'var(--text)' },
            { label: 'Risk/Share',    val: `$${calc.riskPerShare.toFixed(3)}`,       color: 'var(--muted)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 11px' }}>
              <div className="eyebrow" style={{ marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Cheap-calls screener tab ────────────────────────────────────────────────
// Exit plan derived from a call pick — fixed defaults for now (the v3 tracker can learn these later).
// Premium-based: take +60% / stop −40% on the contract price. Underlying-based: time-stop ~10 DTE +
// thesis-break if the stock closes back below its 20-day average (the entry gate, inverted).
const EXIT_TGT = 0.60, EXIT_STOP = 0.40, EXIT_TIME_DTE = 10
function exitPlan(r: any) {
  const entry = (r.cost ?? 0) / 100                 // per-share option premium
  let tsStr = ''
  // Parse expiry as a LOCAL calendar date (not UTC midnight) so the time-stop date doesn't render a
  // day early west of UTC; guard Invalid Date (new Date never throws — it yields NaN). Code review P1/P2.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(r.expiry ?? ''))
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3] - EXIT_TIME_DTE)
    if (Number.isFinite(d.getTime())) tsStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
  return { entry, target: entry * (1 + EXIT_TGT), stop: entry * (1 - EXIT_STOP), tsStr, sma20: r.sma20 as number | undefined }
}

function CallsScanner() {
  const [maxCost, setMaxCost] = useState(800)
  const [data, setData] = useState<any>(null)
  const scan = useMutation({
    mutationFn: () => api.scanner.calls(maxCost),
    onSuccess: (d) => setData(d),
  })

  const TIERS: { key: 'value'|'leverage'|'safe'; label: string; blurb: string }[] = [
    { key: 'value',    label: 'Value',    blurb: 'Balanced — affordable, reasonable odds (Δ .35–.55)' },
    { key: 'leverage', label: 'Leverage', blurb: 'Cheapest, biggest upside, lower odds (Δ .20–.35)' },
    { key: 'safe',     label: 'Safer',    blurb: 'Slightly in-the-money, higher odds (Δ .55–.75)' },
  ]
  const fmt$ = (n: number) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 16, marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Call Scanner — vetted screener</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: 12 }}>
          Finds affordable, liquid call options on names in an uptrend (price above its 20-day average), priced near the money with a reachable breakeven and a tight bid/ask. Each name's best contract is bucketed by risk. Universe: your watchlist + top liquid large-caps. <strong>Analysis only — places no orders.</strong>
          <br /><span style={{ opacity: .85 }}><strong>Exit T/S</strong> = take-profit (+60%) / stop (−40%) on the option price. Also exit by ~10 days to expiry, and bail if the stock closes back below its 20-day average (thesis broke).</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>Max cost / contract
            <select value={maxCost} onChange={e => setMaxCost(parseInt(e.target.value, 10))} style={{ marginLeft: 6, background: 'var(--paper)', color: 'var(--text)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.55rem', padding: '3px 6px' }}>
              {[300, 500, 800, 1200, 2000].map(v => <option key={v} value={v}>${v}</option>)}
            </select>
          </label>
          <button onClick={() => scan.mutate()} disabled={scan.isPending} style={{
            padding: '7px 18px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2,
            fontFamily: 'Syne', fontWeight: 800, fontSize: '.7rem', cursor: scan.isPending ? 'wait' : 'pointer',
          }}>{scan.isPending ? 'Scanning…' : 'Find Calls'}</button>
          {data && <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{data.bullish}/{data.scanned} names bullish</span>}
        </div>
      </div>

      {scan.isError && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)', padding: '8px 0' }}>Scan failed — try again.</div>}

      {data && TIERS.map(t => {
        const rows: any[] = data[t.key] ?? []
        return (
          <div key={t.key} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.8rem', color: 'var(--cash)' }}>{t.label}</span>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{t.blurb}</span>
            </div>
            {rows.length === 0 ? (
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', opacity: .7, padding: '6px 0' }}>No qualifying calls in range.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Syne Mono', fontSize: '.56rem' }}>
                  <thead><tr style={{ color: 'var(--muted)', textAlign: 'right' }}>
                    {['Ticker','Strike','Exp (DTE)','Cost','Δ','IV','Spread','Breakeven','Exit T/S'].map((h, i) => (
                      <th key={h} style={{ padding: '4px 8px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 400, borderBottom: '1px solid var(--edge)' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.ticker}-${r.strike}-${r.expiry}`} style={{ color: 'var(--text)', borderBottom: '1px solid var(--edge)' }}>
                        <td style={{ padding: '5px 8px', fontWeight: 700, color: 'var(--bill)' }}>{r.ticker}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmt$(r.strike)}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.expiry} ({r.dte}d)</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--cash)' }}>{fmt$(r.cost)}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.delta}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{(r.iv * 100).toFixed(0)}%</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.spreadPct}%</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', color: r.pctToBreakeven <= 6 ? 'var(--cash)' : 'var(--muted)' }}>+{r.pctToBreakeven}%</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{(() => { const e = exitPlan(r); return <span><span style={{ color: 'var(--cash)' }}>${e.target.toFixed(2)}</span> / <span style={{ color: 'var(--red)' }}>${e.stop.toFixed(2)}</span></span> })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}

      {data && (data.value.length + data.leverage.length + data.safe.length) === 0 && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '12px 0', textAlign: 'center', border: '1px dashed var(--edge)', borderRadius: 4 }}>
          No qualifying calls — most names are below their 20-day trend right now, or none fit your budget. Try a higher max cost, or check back when the market firms up.
        </div>
      )}
    </div>
  )
}

// v2 — relative-value + momentum-ranked call screener (single conviction list, scored + reasoned).
function SharpCallsScanner() {
  const [maxCost, setMaxCost] = useState(800)
  const [data, setData] = useState<any>(null)
  const scan = useMutation({
    mutationFn: () => api.scanner.sharpCalls(maxCost),
    onSuccess: (d) => setData(d),
  })
  const { data: stats } = useQuery({ queryKey: ['sharp-stats'], queryFn: () => api.scanner.sharpStats(), refetchInterval: 5 * 60_000 })
  const fmt$ = (n: number) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  const scoreColor = (s: number) => s >= 65 ? 'var(--cash)' : s >= 45 ? 'var(--amber)' : 'var(--muted)'
  const ivColor = (r: number) => r < 1.05 ? 'var(--cash)' : r < 1.4 ? 'var(--amber)' : 'var(--red)'
  const rows: any[] = data?.rows ?? []
  const tagChip = (tag: string) => {
    const map: Record<string, { t: string; c: string }> = {
      NEW:       { t: 'NEW',       c: 'var(--cash)' },
      up:        { t: '↑ rising',  c: 'var(--cash)' },
      down:      { t: '↓ fading',  c: 'var(--red)' },
      flat:      { t: '→ steady',  c: 'var(--muted)' },
      recurring: { t: 'recurring', c: 'var(--muted)' },
    }
    const m = map[tag]
    if (!m) return null
    return <span style={{ fontFamily: 'Syne Mono', fontSize: '.45rem', padding: '1px 5px', borderRadius: 2, border: `1px solid ${m.c}`, color: m.c }}>{m.t}</span>
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 16, marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Calls V2_Beta — relative-value + momentum ranked</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: 12 }}>
          One ranked list. Only names in a real uptrend (above their 20- <em>and</em> 50-day average), with a liquid, affordable call near the money. The <strong>score</strong> is point-in-time (0–100): reachable breakeven, relative value (implied vol vs the stock's own realized vol — so you don't overpay), liquidity, and momentum. <strong>Conviction</strong> is a separate history score — it rewards names that keep ranking well across runs, so a one-off flash ranks below a name showing up day after day. Every run is logged (plus auto snapshots at open &amp; post-close), and a <strong>Recently dropped</strong> list shows what fell off since the last run. <strong>Analysis only — places no orders.</strong>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>Max cost / contract
            <select value={maxCost} onChange={e => setMaxCost(parseInt(e.target.value, 10))} style={{ marginLeft: 6, background: 'var(--paper)', color: 'var(--text)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.55rem', padding: '3px 6px' }}>
              {[300, 500, 800, 1200, 2000].map(v => <option key={v} value={v}>${v}</option>)}
            </select>
          </label>
          <button onClick={() => scan.mutate()} disabled={scan.isPending} style={{
            padding: '7px 18px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2,
            fontFamily: 'Syne', fontWeight: 800, fontSize: '.7rem', cursor: scan.isPending ? 'wait' : 'pointer',
          }}>{scan.isPending ? 'Scanning…' : 'Rank Calls'}</button>
          {data && <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{data.bullish}/{data.scanned} names qualified</span>}
        </div>
      </div>

      {/* Self-improving track record + evolving weights */}
      {stats && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div className="eyebrow">Self-improving · track record</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{stats.totalPicksLogged ?? 0} picks logged{stats.tuningHistory?.length > 0 ? ` · tuned ${stats.tuningHistory.length}×` : ''}</div>
          </div>
          {stats.weights && (
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', marginBottom: 10 }}>
              Live weights: <span style={{ color: 'var(--text)' }}>breakeven {Math.round(stats.weights.be * 100)}%</span> · <span style={{ color: 'var(--text)' }}>value {Math.round(stats.weights.iv * 100)}%</span> · <span style={{ color: 'var(--text)' }}>liquidity {Math.round(stats.weights.spread * 100)}%</span> · <span style={{ color: 'var(--text)' }}>momentum {Math.round(stats.weights.mom * 100)}%</span>
            </div>
          )}
          {(() => {
            const withData = (stats.horizons ?? []).filter((h: any) => h.n > 0)
            if (!withData.length) return <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', opacity: .75 }}>Building history — each pick's outcome is measured 1/2/3 weeks out. Hit-rate stats and automatic weight-tuning kick in as picks mature (auto-snapshots run at open &amp; close every weekday).</div>
            return (
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                {withData.map((h: any) => (
                  <div key={h.horizon} style={{ minWidth: 130 }}>
                    <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.62rem', color: 'var(--text)', marginBottom: 2 }}>{h.horizon / 7}-week</div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                      <span style={{ color: h.breakevenRate >= 50 ? 'var(--cash)' : 'var(--text)' }}>{h.breakevenRate}% hit breakeven</span><br />
                      {h.upRate}% up · avg {h.avgMove >= 0 ? '+' : ''}{h.avgMove}% <span style={{ opacity: .6 }}>(n={h.n})</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      )}

      {scan.isError && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)', padding: '8px 0' }}>Scan failed — try again.</div>}

      {data && rows.length === 0 && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '12px 0', textAlign: 'center', border: '1px dashed var(--edge)', borderRadius: 4 }}>
          No qualifying calls — few names are in a clean uptrend right now, or none fit your budget. Try a higher max cost, or check back when the market firms up.
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r, i) => (
            <div key={`${r.ticker}-${r.strike}-${r.expiry}`} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {/* Score (point-in-time) */}
                <div style={{ minWidth: 38, textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.05rem', color: scoreColor(r.score) }}>{Math.round(r.score)}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>score</div>
                </div>
                {/* Conviction (history) */}
                <div style={{ minWidth: 44, textAlign: 'center', borderLeft: '1px solid var(--edge)', paddingLeft: 8 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.05rem', color: scoreColor(r.conviction ?? 0) }}>{r.conviction ?? '—'}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>conviction</div>
                </div>
                {/* Ticker + contract */}
                <div style={{ minWidth: 120 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: 'var(--bill)' }}>{r.ticker} <span style={{ fontSize: '.62rem', color: 'var(--muted)', fontWeight: 400 }}>{fmt$(r.strike)}C</span></div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                    <span>{r.expiry} · {r.dte}d · Δ{r.delta}</span>
                    {tagChip(r.tag)}
                    {r.daysSeen > 1 && <span style={{ opacity: .7 }}>· seen {r.daysSeen}d</span>}
                  </div>
                </div>
                {/* Cost */}
                <div style={{ minWidth: 60 }}>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--cash)' }}>{fmt$(r.cost)}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>per contract</div>
                </div>
                {/* Relative value */}
                <div style={{ minWidth: 70 }}>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: ivColor(r.ivRatio) }}>{r.ivRatio}× IV/rv</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>relative value</div>
                </div>
                {/* Breakeven */}
                <div style={{ minWidth: 60 }}>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: r.pctToBreakeven <= 6 ? 'var(--cash)' : 'var(--text)' }}>+{r.pctToBreakeven}%</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>to breakeven</div>
                </div>
                {/* Momentum */}
                <div style={{ minWidth: 64 }}>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--text)' }}>+{r.trendPct}% · {r.relVol}×</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.42rem', color: 'var(--muted)' }}>trend · rel-vol</div>
                </div>
              </div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)', marginTop: 6, opacity: .8 }}>{r.why}</div>
              {(() => { const e = exitPlan(r); return (
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)', marginTop: 5, borderTop: '1px dashed var(--edge)', paddingTop: 5 }}>
                  <span style={{ color: 'var(--text)' }}>Exit plan:</span> take ${e.target.toFixed(2)} <span style={{ color: 'var(--cash)' }}>(+60%)</span> · stop ${e.stop.toFixed(2)} <span style={{ color: 'var(--red)' }}>(−40%)</span>{e.tsStr ? <> · by {e.tsStr} <span style={{ opacity: .6 }}>(10 DTE)</span></> : null}{e.sma20 ? <> · bail if {r.ticker} closes &lt; ${e.sma20.toFixed(2)} <span style={{ opacity: .6 }}>(20d)</span></> : null}
                </div>
              )})()}
            </div>
          ))}
        </div>
      )}

      {data?.dropped?.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Recently dropped — ranked last run, no longer qualifying</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.dropped.map((d: any, i: number) => (
              <div key={d.ticker} style={{ background: 'var(--paper)', border: '1px dashed var(--edge)', borderRadius: 3, padding: '6px 10px' }}>
                <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.7rem', color: 'var(--text)' }}>{d.ticker}</span>
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)', marginLeft: 6 }}>was {Math.round(d.lastScore)} · {new Date(d.lastSeen).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', opacity: .7, marginTop: 6 }}>Fell off since the previous run — likely broke its uptrend, re-priced richer, or its best contract moved out of range.</div>
        </div>
      )}
    </div>
  )
}

// Auto Calls — the scanner trades its own picks on paper, mechanically. Entries: top post-close
// Sharp picks (score ≥ 50; skips names already held or with earnings before expiry). Exits:
// +60% target / −40% stop / ≤10 DTE / underlying closes under its 20d SMA. Real option quotes,
// deterministic ±2% fill haircut. Read-only view of the engine's positions + ledger.
function AutoCallsPanel() {
  const { data, isLoading } = useQuery({ queryKey: ['auto-calls-portfolio'], queryFn: () => api.scanner.portfolio(), refetchInterval: 5 * 60_000 })
  const fmt$ = (n: number) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  const pnl$ = (n: number | null | undefined) => n == null ? '—' : `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(2)}`
  const pnlClr = (n: number | null | undefined) => n == null ? 'var(--muted)' : n >= 0 ? 'var(--cash)' : 'var(--red)'
  const reasonChip = (r: string) => {
    const map: Record<string, { t: string; c: string }> = {
      target:  { t: 'target +60%', c: 'var(--cash)' },
      stop:    { t: 'stop −40%',   c: 'var(--red)' },
      time:    { t: '10-DTE time', c: 'var(--amber)' },
      thesis:  { t: 'trend broke', c: 'var(--amber)' },
      expired: { t: 'expired',     c: 'var(--muted)' },
    }
    const m = map[r] ?? { t: r, c: 'var(--muted)' }
    return <span style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', padding: '1px 6px', borderRadius: 2, border: `1px solid ${m.c}`, color: m.c }}>{m.t}</span>
  }
  const s = data?.stats
  const total = s ? +(s.realized + s.unrealized).toFixed(2) : null

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 16, marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Auto Calls — the scanner trades its own picks (paper)</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', lineHeight: 1.7 }}>
          Every post-close run, the engine buys the top ranked calls (~$500 each, real option quotes, 2% slippage), skipping names already held or with <strong>earnings before expiry</strong>. It exits mechanically — <strong>+60% target · −40% stop · 10-DTE time-stop · trend-break</strong> — and books realized P&L below. This is the scanner's live track record: if this curve can't make paper money, no real-money version deserves to exist. <strong>Fully virtual — no broker, no orders.</strong>
        </div>
      </div>

      {isLoading && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '12px 0' }}>Loading portfolio…</div>}

      {s && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Total P&L', val: pnl$(total), clr: pnlClr(total) },
            { label: 'Realized', val: pnl$(s.realized), clr: pnlClr(s.realized) },
            { label: 'Unrealized', val: pnl$(s.unrealized), clr: pnlClr(s.unrealized) },
            { label: 'Win rate', val: s.winRate != null ? `${s.winRate}%` : '—', clr: 'var(--text)' },
            { label: 'Profit factor', val: s.profitFactor != null ? String(s.profitFactor) : '—', clr: 'var(--text)' },
            { label: 'Open / Closed', val: `${s.openCount} / ${s.closedCount}`, clr: 'var(--text)' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.46rem', color: 'var(--muted)', letterSpacing: '.08em', marginBottom: 3 }}>{c.label.toUpperCase()}</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: c.clr }}>{c.val}</div>
            </div>
          ))}
        </div>
      )}

      {data && data.open?.length === 0 && data.closed?.length === 0 && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '14px 0', textAlign: 'center', border: '1px dashed var(--edge)', borderRadius: 4, marginBottom: 14 }}>
          Armed and waiting — the engine enters its first positions at the next post-close run (~4:05pm ET, weekdays), from that day's top-ranked picks.
        </div>
      )}

      {data?.open?.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Open positions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.open.map((p: any) => (
              <div key={p.id} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)' }}>{p.ticker} <span style={{ fontSize: '.6rem', color: 'var(--muted)', fontWeight: 400 }}>{fmt$(p.strike)}C</span></div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{p.expiry} · {p.qty}× · score {p.score != null ? Math.round(p.score) : '—'}</div>
                </div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>
                  in @ ${p.entryPrice?.toFixed(2)} → mark {p.lastMark != null ? `$${p.lastMark.toFixed(2)}` : '—'}
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.75rem', fontWeight: 700, color: pnlClr(p.pnl) }}>{pnl$(p.pnl)}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: pnlClr(p.pnl) }}>{p.pnlPct != null ? `${p.pnlPct >= 0 ? '+' : ''}${p.pnlPct}%` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.closed?.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Closed trades</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.closed.map((p: any, i: number) => (
              <div key={`${p.ticker}-${p.closedAt}-${i}`} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.75rem', minWidth: 110, color: 'var(--text)' }}>{p.ticker} {fmt$(p.strike)}C <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{p.expiry}</span></div>
                {reasonChip(p.exitReason)}
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)' }}>
                  {p.qty}× · ${p.entryPrice?.toFixed(2)} → ${p.exitPrice?.toFixed(2)} · {p.closedAt ? new Date(p.closedAt * 1000).toLocaleDateString() : ''}
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.7rem', fontWeight: 700, color: pnlClr(p.realizedPnl) }}>{pnl$(p.realizedPnl)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
